import React, { FC } from 'react';

import cx from 'clsx';
import { Icon } from '@mdi/react';

import { ErrorDisplay } from 'components';
import {
  conferenceStatusSelector,
  connectedSelector,
  errorSelector,
  statusSelector,
} from 'features/details/dcc';
import { useSelector } from 'react-redux';
import { DccStatus, FetchDccRecordingReturn } from 'types';
import { AlarmCallType } from '@x-guard/xgac-types/xgac';
import { useLocale } from 'lib';
import { mdiPhoneOff } from '@mdi/js';
import { dccRecordingSrcSelector } from 'features/details/dcc/recording';
import { ackedSelector } from 'features/details/ack/exports';
import { phoneCallsSelector, phoneCallsStatusSelector } from 'features/details/phoneCalls';
import Participants from './participants';
import Disconnected from './disconnected';
import Connected from './connected';
import styles from './style.module.scss';
import Recording from './recording';
import FirstMomentRecording from './firstMomentRecording';
import DCCLoader from './loader';
import DCCDestroyed from './destroyed';
import PhoneCall from './phoneCall';

type DCCSwitchProps = {
  dccStatus: DccStatus;
  recording: FetchDccRecordingReturn;
};

const DCCSwitch: FC<DCCSwitchProps> = ({ recording, dccStatus }) => {

  const connected = useSelector(connectedSelector);
  const { t } = useLocale();
  const helperCallOpen = useSelector(phoneCallsSelector).some(({ call }) => call.type === AlarmCallType.HelperCall);

  if (dccStatus === DccStatus.Created) {

    return (
      <div className={styles.created}>
        {connected ? <Connected/> : <Disconnected/>}
        <Participants/>
      </div>
    );

  }

  if (recording) {

    return <Recording recording={recording}/>;

  }

  if (dccStatus === DccStatus.Destroyed) {

    return <DCCDestroyed/>;

  }

  if (dccStatus === DccStatus.NotCreated) {

    return (
      <div className={cx(styles.shortStatus, styles.notCreated)}>
        <Icon path={mdiPhoneOff}/>
        <span>{t(helperCallOpen ? 'Alarm_Detail_DCC.Not_Created_Helper_Call' : 'Alarm_Detail_DCC.Not_Created')}</span>
      </div>
    );

  }

  return null;

};

export const DynamicConferenceCall: FC = () => {

  const status = useSelector(statusSelector);
  const error = useSelector(errorSelector);
  const conferenceStatus = useSelector(conferenceStatusSelector);
  const recording = useSelector(dccRecordingSrcSelector);
  const acked = useSelector(ackedSelector);
  const phoneCalls = useSelector(phoneCallsSelector);
  const phoneCallsStatus = useSelector(phoneCallsStatusSelector);

  if (status.idle || acked) return null;

  const legacyCreated = status.fulfilled && conferenceStatus === DccStatus.Created;
  // Until the main api answered we cannot tell whether "no call" is true.
  const waitingForPhoneCalls = !phoneCallsStatus.fulfilled && !phoneCallsStatus.rejected;
  // A failed main api lookup must not read as "no call" (the call may still be running).
  const phoneCallsFailed = phoneCallsStatus.rejected && !legacyCreated;
  /**
   * This box is for the alarm call only (helper calls live in the helpers block). Main api alarm calls take
   * priority; the legacy conference stays for when the main api has none (also for its "no call" state).
   */
  const alarmCalls = phoneCalls.filter(({ call }) => call.type === AlarmCallType.AlarmCall);
  const showLegacy = alarmCalls.length === 0;

  return (
    <>
      {showLegacy && (
        <div
          tabIndex={-1}
          data-guide-target="alarmCall"
          className={cx(
            styles.dynamicConferenceCall,
          )}
        >
          {/**
            * If we loaded, and a conference exists, display either the disconnected or connected view,
            * depending on the connected state of the user
            */}
          {(status.pending || (waitingForPhoneCalls && !legacyCreated)) && (
            <DCCLoader/>
          )}
          {status.fulfilled && !(waitingForPhoneCalls && !legacyCreated) && !phoneCallsFailed && (
            <DCCSwitch
              recording={recording}
              dccStatus={conferenceStatus}
            />
          )}
          {status.rejected && (
            <ErrorDisplay {...error}/>
          )}
          {status.fulfilled && phoneCallsFailed && (
            <ErrorDisplay/>
          )}
        </div>
      )}
      {alarmCalls.length > 0 && (
        <div tabIndex={-1} data-guide-target="alarmCall" className={styles.dynamicConferenceCall}>
          {alarmCalls.map((phoneCall) => (
            <PhoneCall key={phoneCall.call._id} phoneCall={phoneCall}/>
          ))}
        </div>
      )}
      <div className={styles.dynamicConferenceCall}>
        <FirstMomentRecording/>
      </div>
    </>
  );

};
